import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { codexApi } from '../api';
import AudioButton from '../components/AudioButton';
import { BookMarked } from 'lucide-react';

export default function CodexBook() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    codexApi.personalCodex()
      .then(setData)
      .catch(() => setData({ items: [], stats: { total: 0, hebrew: 0, greek: 0 } }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', opacity: 0.7 }}>Opening your Codex…</div>;

  const items = (data?.items || []).filter((i) => filter === 'all' || i.language === filter);

  return (
    <div data-testid="codex-personal-book" style={{ paddingTop: 8 }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 18 }}>
        <div className="codex-eyebrow">YOUR PERSONAL CODEX</div>
        <h1 className="codex-h1" style={{ marginTop: 6 }}>The Book You Are Writing</h1>
        <p style={{ opacity: 0.75, marginTop: 6 }}>
          {data.stats.total === 0
            ? "Your codex is empty — for now. Decode your first scroll to begin filling its pages."
            : `${data.stats.total} mastered · ${data.stats.hebrew} Hebrew · ${data.stats.greek} Greek`}
        </p>
      </motion.div>

      {data.stats.total > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['all', 'hebrew', 'greek'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="codex-btn codex-btn-ghost"
              style={{
                padding: '8px 14px',
                fontSize: 11,
                background: filter === f ? 'rgba(212,175,55,0.18)' : undefined,
                borderColor: filter === f ? 'rgba(212,175,55,0.6)' : undefined,
              }}
              data-testid={`codex-book-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      )}

      {items.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="codex-card"
          style={{ padding: 60, textAlign: 'center' }}
        >
          <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} style={{ display: 'inline-block' }}>
            <BookMarked size={56} color="var(--codex-gold-light)" />
          </motion.div>
          <div className="codex-display" style={{ marginTop: 18, fontSize: 22 }}>The first page awaits.</div>
          <div style={{ opacity: 0.75, marginTop: 6, fontSize: 14 }}>Open today's scroll to mark your first word.</div>
        </motion.div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {items.map((it, i) => (
            <motion.button
              key={it.word_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.04, 0.4) }}
              whileHover={{ y: -3 }}
              onClick={() => setActive(it)}
              className="codex-card"
              style={{ padding: 18, textAlign: 'left', cursor: 'pointer', color: 'var(--codex-parchment)' }}
              data-testid={`codex-book-item-${it.word_id}`}
            >
              <div className="codex-eyebrow" style={{ color: it.language === 'hebrew' ? 'var(--codex-gold-light)' : '#a78bfa' }}>
                {it.language.toUpperCase()} {it.strongs ? `· ${it.strongs}` : ''}
              </div>
              <div
                className={it.language === 'hebrew' ? 'codex-hebrew' : 'codex-greek'}
                style={{ fontSize: 32, color: 'var(--codex-gold-light)', marginTop: 8, lineHeight: 1 }}
              >
                {it.script}
              </div>
              <div className="codex-display" style={{ marginTop: 6, fontSize: 18 }}>{it.transliteration}</div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{it.short_meaning}</div>
            </motion.button>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setActive(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(11, 10, 22, 0.85)', backdropFilter: 'blur(8px)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          data-testid="codex-book-detail-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="codex-card-gold codex-card"
            style={{ padding: 30, maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div className="codex-eyebrow">{active.language.toUpperCase()} {active.strongs ? `· ${active.strongs}` : ''}</div>
            <div className={active.language === 'hebrew' ? 'codex-hebrew' : 'codex-greek'} style={{ fontSize: 64, color: 'var(--codex-gold-light)', textAlign: 'center', margin: '12px 0' }}>{active.script}</div>
            <div className="codex-display" style={{ fontSize: 28, textAlign: 'center', marginBottom: 4 }}>{active.transliteration}</div>
            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              <AudioButton wordId={active.word_id} label="Hear it" />
              <div style={{ opacity: 0.7, fontSize: 13, marginTop: 6 }}>{active.pronunciation_hint}</div>
            </div>

            <div style={{ marginTop: 18 }}>
              <div className="codex-eyebrow">LITERAL</div>
              <p style={{ lineHeight: 1.7, marginTop: 6 }}>{active.literal}</p>
            </div>
            <div style={{ marginTop: 14 }}>
              <div className="codex-eyebrow">CULTURAL</div>
              <p style={{ lineHeight: 1.7, marginTop: 6 }}>{active.cultural}</p>
            </div>
            <div style={{ marginTop: 14 }}>
              <div className="codex-eyebrow">PROPHETIC</div>
              <p style={{ lineHeight: 1.7, marginTop: 6 }}>{active.prophetic}</p>
            </div>
            <div className="codex-card" style={{ padding: 14, marginTop: 18 }}>
              <div className="codex-eyebrow">REFLECT</div>
              <p style={{ lineHeight: 1.7, marginTop: 6 }}>{active.reflection_prompt}</p>
            </div>

            <button onClick={() => setActive(null)} className="codex-btn codex-btn-ghost" style={{ marginTop: 18, width: '100%' }}>
              Close
            </button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
